import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { InteractiveResult, InteractiveSpec } from '@aurora/core'

/**
 * @aurora/interactives — the hand-built activity renderers.
 *
 * Two ways an activity reaches the child, by design (docs/07-synthesis.md):
 *   1. Agent-authored HTML in a sandboxed iframe — fully open, the agent can
 *      make anything. That path lives in the host app (Kid Mode).
 *   2. These renderers — a quality shelf for the common shapes, hand-built so
 *      they are correct, instrumented, and consistent. The agent reaches for
 *      a spec when one fits and writes HTML when it does not.
 *
 * Renderer rules:
 *   - Touch targets 64px minimum; a toddler's finger is a thumbprint, not a cursor.
 *   - Results are emitted through onResult exactly once, with per-item detail,
 *     because playing IS an observation.
 *   - Wrong answers get a gentle wobble and another chance — never a buzzer,
 *     never a score on screen.
 *   - All colors come from the host theme via CSS custom properties.
 */

export interface RendererProps {
  spec: InteractiveSpec
  theme: 'light' | 'dark'
  onResult: (result: InteractiveResult) => void
  onProgress?: (progress: { step: number; of: number }) => void
}

export function InteractiveRenderer(props: RendererProps) {
  switch (props.spec.kind) {
    case 'sight_word_deck':
      return <SightWordDeck {...props} />
    case 'memory_pairs':
      return <MemoryPairs {...props} />
    case 'counting_game':
      return <CountingGame {...props} />
    case 'deduction_puzzle':
      return <DeductionPuzzle {...props} />
    default:
      return <NotYetPlayable {...props} />
  }
}

/** True when this package has a hand-built renderer for the spec's kind. */
export function isRenderable(kind: string): boolean {
  return ['sight_word_deck', 'memory_pairs', 'counting_game', 'deduction_puzzle'].includes(kind)
}

// ─────────────────────────────────────────────────────────────
// Shared result plumbing
// ─────────────────────────────────────────────────────────────

interface ItemOutcome {
  id: string
  correct: boolean
  attempts: number
  ms: number
}

/** One-shot result emitter with per-item timing. */
function useResultCollector(
  spec: InteractiveSpec,
  onResult: (r: InteractiveResult) => void,
) {
  const startedAt = useRef(Date.now())
  const itemStart = useRef(Date.now())
  const items = useRef<ItemOutcome[]>([])
  const emitted = useRef(false)

  const recordItem = useCallback((id: string, correct: boolean, attempts: number) => {
    items.current.push({ id, correct, attempts, ms: Date.now() - itemStart.current })
    itemStart.current = Date.now()
  }, [])

  const finish = useCallback(
    (completed: boolean) => {
      if (emitted.current) return
      emitted.current = true
      const list = items.current
      onResult({
        specKind: spec.kind,
        startedAt: startedAt.current,
        completedAt: Date.now(),
        durationMs: Date.now() - startedAt.current,
        completed,
        score: list.length
          ? { correct: list.filter((i) => i.correct).length, total: list.length }
          : undefined,
        items: list,
      })
    },
    [spec.kind, onResult],
  )

  return { recordItem, finish }
}

/** Fisher–Yates with a seed, so a shuffled deck is reproducible per spec. */
function seededShuffle<T>(input: readonly T[], seed: number): T[] {
  const out = [...input]
  let s = seed || 1
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 16807) % 2147483647
    const j = s % (i + 1)
    ;[out[i], out[j]] = [out[j]!, out[i]!]
  }
  return out
}

// ─────────────────────────────────────────────────────────────
// Sight word deck — the fluency workhorse
// ─────────────────────────────────────────────────────────────

function SightWordDeck({ spec, onResult, onProgress }: RendererProps) {
  const config = spec.config as any
  const words: string[] = useMemo(() => {
    const raw = (config.words ?? []).map((w: any) =>
      typeof w === 'string' ? w : w?.word ?? '',
    ).filter(Boolean)
    return config.order === 'shuffled' || config.shuffle
      ? seededShuffle(raw, config.shuffleSeed ?? config.seed ?? 7)
      : raw
  }, [config])

  const { recordItem, finish } = useResultCollector(spec, onResult)
  const [index, setIndex] = useState(0)
  const [wobble, setWobble] = useState(false)
  const attempts = useRef(1)

  const word = words[index]

  const answer = (gotIt: boolean) => {
    if (!word) return
    if (gotIt) {
      recordItem(word, attempts.current === 1, attempts.current)
      attempts.current = 1
      const next = index + 1
      onProgress?.({ step: next, of: words.length })
      if (next >= words.length) finish(true)
      else setIndex(next)
    } else {
      // "Still working on it" is one tap for the parent, one more look for the
      // child; the miss is only recorded when the card is finally passed.
      attempts.current += 1
      setWobble(true)
      window.setTimeout(() => setWobble(false), 350)
    }
  }

  if (!words.length) return <NotYetPlayable spec={spec} theme="light" onResult={onResult} />
  if (!word) return null

  return (
    <div style={S.stage}>
      <div
        key={word}
        style={{ ...S.bigCard, ...(wobble ? S.wobble : null) }}
        aria-label={`Word card: ${word}`}
      >
        <span style={S.cardWord}>{word}</span>
      </div>
      <div style={S.dots} aria-hidden="true">
        {words.map((w, i) => (
          <span key={w + i} style={{ ...S.dot, opacity: i <= index ? 1 : 0.25 }} />
        ))}
      </div>
      {/* The buttons are the PARENT's controls, phrased for the parent. */}
      <div style={S.row}>
        <button style={{ ...S.btn, ...S.btnQuiet }} onClick={() => answer(false)}>
          One more look
        </button>
        <button style={{ ...S.btn, ...S.btnGo }} onClick={() => answer(true)}>
          She read it!
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Memory pairs
// ─────────────────────────────────────────────────────────────

interface MemoryCard {
  key: string
  face: string
  pair: string
}

function MemoryPairs({ spec, onResult, onProgress }: RendererProps) {
  const config = spec.config as any
  const cards: MemoryCard[] = useMemo(() => {
    const faces: string[] = (config.pairs ?? config.items ?? [])
      .map((p: any) => (typeof p === 'string' ? p : p?.emoji ?? p?.face ?? p?.label ?? ''))
      .filter(Boolean)
      .slice(0, config.pairCount ?? 6)
    const doubled = faces.flatMap((face: string, i: number) => [
      { key: `${i}a`, face, pair: String(i) },
      { key: `${i}b`, face, pair: String(i) },
    ])
    return seededShuffle(doubled, config.seed ?? 11)
  }, [config])

  const { recordItem, finish } = useResultCollector(spec, onResult)
  const [faceUp, setFaceUp] = useState<string[]>([])
  const [matched, setMatched] = useState<Set<string>>(new Set())
  const misses = useRef(0)
  const lock = useRef(false)

  const tap = (card: MemoryCard) => {
    if (lock.current || matched.has(card.pair) || faceUp.includes(card.key)) return

    if (faceUp.length === 0) {
      setFaceUp([card.key])
      return
    }

    const firstKey = faceUp[0]!
    const first = cards.find((c) => c.key === firstKey)!
    setFaceUp([firstKey, card.key])
    lock.current = true

    if (first.pair === card.pair) {
      window.setTimeout(() => {
        const next = new Set(matched)
        next.add(card.pair)
        setMatched(next)
        setFaceUp([])
        lock.current = false
        recordItem(card.face, misses.current === 0, misses.current + 1)
        misses.current = 0
        onProgress?.({ step: next.size, of: cards.length / 2 })
        if (next.size === cards.length / 2) finish(true)
      }, 450)
    } else {
      misses.current += 1
      window.setTimeout(() => {
        setFaceUp([])
        lock.current = false
      }, 900)
    }
  }

  if (!cards.length) return <NotYetPlayable spec={spec} theme="light" onResult={onResult} />

  const columns = Math.ceil(Math.sqrt(cards.length))
  return (
    <div style={S.stage}>
      <div style={{ ...S.grid, gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {cards.map((card) => {
          const up = faceUp.includes(card.key) || matched.has(card.pair)
          return (
            <button
              key={card.key}
              style={{
                ...S.memCard,
                background: up ? 'var(--canvas, #fff)' : 'var(--accent-wash, #eee)',
                transform: up ? 'rotateY(0deg)' : 'rotateY(180deg)',
                opacity: matched.has(card.pair) ? 0.55 : 1,
              }}
              onClick={() => tap(card)}
              aria-label={up ? card.face : 'Face-down card'}
            >
              {up ? card.face : ''}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Counting game — 1:1 correspondence, tap to count
// ─────────────────────────────────────────────────────────────

function CountingGame({ spec, onResult, onProgress }: RendererProps) {
  const config = spec.config as any
  const emoji = config.objectEmoji ?? config.emoji ?? '🦖'
  const target = Math.max(1, Math.min(20, config.count ?? config.targetNumber ?? 5))
  const positions = useMemo(() => {
    // Scattered but deterministic, and never overlapping the count readout.
    return Array.from({ length: target }, (_, i) => {
      const seed = (config.seed ?? 3) + i * 37
      return {
        left: 8 + ((seed * 16807) % 76),
        top: 12 + ((seed * 48271) % 58),
      }
    })
  }, [target, config.seed])

  const { recordItem, finish } = useResultCollector(spec, onResult)
  const [counted, setCounted] = useState<Set<number>>(new Set())

  const tap = (i: number) => {
    if (counted.has(i)) return
    const next = new Set(counted)
    next.add(i)
    setCounted(next)
    onProgress?.({ step: next.size, of: target })
    if (next.size === target) {
      recordItem(`count-${target}`, true, 1)
      window.setTimeout(() => finish(true), 900)
    }
  }

  return (
    <div style={{ ...S.stage, position: 'relative', minHeight: 380 }}>
      <div style={S.countReadout} aria-live="polite">
        {counted.size > 0 ? counted.size : ''}
      </div>
      {positions.map((pos, i) => (
        <button
          key={i}
          style={{
            ...S.countItem,
            left: `${pos.left}%`,
            top: `${pos.top}%`,
            transform: counted.has(i) ? 'scale(1.25)' : 'scale(1)',
            filter: counted.has(i) ? 'grayscale(0)' : 'grayscale(0.15)',
            opacity: counted.has(i) ? 0.9 : 1,
          }}
          onClick={() => tap(i)}
          aria-label={counted.has(i) ? 'Counted' : 'Tap to count'}
        >
          {emoji}
          {counted.has(i) && (
            <span style={S.countBadge}>{[...counted].sort((a, b) => a - b).indexOf(i) + 1}</span>
          )}
        </button>
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Deduction puzzle — who took the cookie?
// ─────────────────────────────────────────────────────────────

function DeductionPuzzle({ spec, onResult }: RendererProps) {
  const config = spec.config as any
  const candidates: Array<{ id: string; emoji: string; label?: string }> = useMemo(
    () =>
      (config.candidates ?? []).map((c: any, i: number) =>
        typeof c === 'string'
          ? { id: String(i), emoji: c }
          : { id: c?.id ?? String(i), emoji: c?.emoji ?? '🙂', label: c?.label },
      ),
    [config],
  )
  const clues: string[] = useMemo(
    () => (config.clues ?? []).map((c: any) => (typeof c === 'string' ? c : c?.text ?? '')),
    [config],
  )
  const solutionId = String(config.solution ?? config.solutionId ?? '')

  const { recordItem, finish } = useResultCollector(spec, onResult)
  const [eliminated, setEliminated] = useState<Set<string>>(new Set())
  const [solved, setSolved] = useState(false)
  const wrongTaps = useRef(0)

  const tap = (id: string) => {
    if (solved) return
    if (id === solutionId) {
      setSolved(true)
      recordItem('deduction', wrongTaps.current === 0, wrongTaps.current + 1)
      window.setTimeout(() => finish(true), 1200)
    } else {
      wrongTaps.current += 1
      const next = new Set(eliminated)
      next.add(id)
      setEliminated(next)
    }
  }

  if (!candidates.length || !solutionId) {
    return <NotYetPlayable spec={spec} theme="light" onResult={onResult} />
  }

  return (
    <div style={S.stage}>
      <div style={S.clueBox}>
        {clues.map((clue, i) => (
          <p key={i} style={S.clue}>{clue}</p>
        ))}
      </div>
      <div style={S.row}>
        {candidates.map((c) => (
          <button
            key={c.id}
            style={{
              ...S.suspect,
              opacity: eliminated.has(c.id) ? 0.25 : 1,
              outline: solved && c.id === solutionId ? '4px solid var(--signal-progress, #2C8F5C)' : 'none',
            }}
            onClick={() => tap(c.id)}
            aria-label={c.label ?? 'Suspect'}
          >
            {c.emoji}
          </button>
        ))}
      </div>
      {solved && <p style={S.solvedNote}>That's the one! How did you know?</p>}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// The honest fallback
// ─────────────────────────────────────────────────────────────

function NotYetPlayable({ spec, onResult }: RendererProps) {
  useEffect(() => {
    // Emit nothing — an unplayable activity is not an observation.
    void onResult
  }, [onResult])
  return (
    <div style={S.stage}>
      <div style={S.fallback}>
        <p style={S.fallbackTitle}>{spec.title}</p>
        <p style={S.fallbackBody}>
          This kind of activity does not have a built-in player yet. Ask Aurora
          to build it as a playable page instead — that always works.
        </p>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// Styles — theme tokens with fallbacks so the package stands alone
// ─────────────────────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  stage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    padding: 24,
    minHeight: 320,
    width: '100%',
    fontFamily: 'inherit',
  },
  bigCard: {
    display: 'grid',
    placeItems: 'center',
    minWidth: 260,
    minHeight: 160,
    padding: '32px 48px',
    borderRadius: 24,
    background: 'var(--canvas, #fff)',
    border: '2px solid var(--line-strong, #ddd)',
    boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
  },
  cardWord: {
    fontSize: 64,
    fontWeight: 650,
    letterSpacing: '0.01em',
    color: 'var(--ink, #1a1815)',
  },
  wobble: { animation: 'aurora-wobble 320ms ease-in-out' },
  dots: { display: 'flex', gap: 6 },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: 'var(--accent, #6B5BD8)',
  },
  row: { display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' },
  btn: {
    minHeight: 64,
    minWidth: 140,
    padding: '0 28px',
    borderRadius: 999,
    fontSize: 18,
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
  },
  btnGo: { background: 'var(--ink, #1a1815)', color: 'var(--bg, #fff)' },
  btnQuiet: {
    background: 'transparent',
    color: 'var(--ink-muted, #6b6560)',
    border: '2px solid var(--line, #e5e0d8)',
  },
  grid: { display: 'grid', gap: 12, width: '100%', maxWidth: 480 },
  memCard: {
    aspectRatio: '1',
    minHeight: 72,
    borderRadius: 16,
    fontSize: 40,
    border: '2px solid var(--line, #e5e0d8)',
    cursor: 'pointer',
    transition: 'transform 300ms ease, opacity 300ms ease, background 300ms ease',
  },
  countReadout: {
    position: 'absolute',
    top: 8,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 56,
    fontWeight: 700,
    color: 'var(--accent, #6B5BD8)',
    pointerEvents: 'none',
  },
  countItem: {
    position: 'absolute',
    fontSize: 52,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    minWidth: 64,
    minHeight: 64,
    transition: 'transform 200ms ease',
  },
  countBadge: {
    position: 'absolute',
    top: -6,
    right: -6,
    width: 26,
    height: 26,
    borderRadius: 999,
    background: 'var(--accent, #6B5BD8)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
  },
  clueBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '16px 24px',
    borderRadius: 16,
    background: 'var(--surface, #f6f2ea)',
    maxWidth: 420,
  },
  clue: { margin: 0, fontSize: 20, lineHeight: 1.5, color: 'var(--ink, #1a1815)' },
  suspect: {
    fontSize: 72,
    minWidth: 96,
    minHeight: 96,
    borderRadius: 20,
    background: 'var(--canvas, #fff)',
    border: '2px solid var(--line, #e5e0d8)',
    cursor: 'pointer',
    transition: 'opacity 250ms ease',
  },
  solvedNote: { fontSize: 20, fontWeight: 600, color: 'var(--signal-progress, #2C8F5C)' },
  fallback: {
    textAlign: 'center',
    maxWidth: 380,
    padding: 24,
    borderRadius: 16,
    background: 'var(--surface, #f6f2ea)',
  },
  fallbackTitle: { fontSize: 20, fontWeight: 650, margin: '0 0 8px' },
  fallbackBody: { fontSize: 15, lineHeight: 1.6, color: 'var(--ink-muted, #6b6560)', margin: 0 },
}
